import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { BACKEND_URL } from "../config";
import "./CalendarView.mobile.css";

const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfCalendarGrid(monthDate) {
  const first = startOfMonth(monthDate);
  const day = first.getDay(); // Sun=0
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - day);
  return gridStart;
}
function endOfCalendarGrid(monthDate) {
  const last = endOfMonth(monthDate);
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay())); // extend to Saturday
  return end;
}
function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function inSameMonth(d, monthDate) {
  return (
    d.getFullYear() === monthDate.getFullYear() &&
    d.getMonth() === monthDate.getMonth()
  );
}
function isoDateFromStartAt(start_at) {
  return String(start_at || "").slice(0, 10);
}
function fmtTime(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  return `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}
function toLocalDatetimeInputValue(iso) {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(
    dt.getDate()
  )}T${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}
function toLocalDateInputValue(iso) {
  return iso ? String(iso).slice(0, 10) : "";
}
function isoStartOfDay(dateStr) {
  return dateStr ? `${dateStr}T00:00:00` : null;
}
function isoEndOfDayExclusive(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(
    d.getDate()
  )}T00:00:00`;
}

function formatDayHeading(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "Day";
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
function normalizeDateOnly(value) {
  if (!value) return "";
  if (value instanceof Date) return toISODate(value);
  const s = String(value).trim();
  if (!s) return "";
  const m = s.match(/\d{4}-\d{2}-\d{2}/);
  return m ? m[0] : "";
}
function buildGoalCalendarItems(rawGoals, rangeStart, rangeEnd) {
  const startKey = toISODate(rangeStart);
  const endKey = toISODate(rangeEnd);
  const items = [];
  const pushIfInRange = (item) => {
    if (!item?.start_at) return;
    const key = normalizeDateOnly(item.start_at);
    if (!key || key < startKey || key > endKey) return;
    items.push(item);
  };

  for (const goal of Array.isArray(rawGoals) ? rawGoals : []) {
    const goalId = goal?.goal_id ?? goal?.id ?? goal?._id;
    if (!goalId) continue;
    const goalTitle = goal?.title ?? goal?.name ?? "Goal";
    const goalDescription = goal?.description ?? goal?.notes ?? "";
    const goalDue =
      normalizeDateOnly(goal?.due_date) ||
      normalizeDateOnly(goal?.due_at) ||
      normalizeDateOnly(goal?.target_date) ||
      normalizeDateOnly(goal?.deadline) ||
      normalizeDateOnly(goal?.end_date);

    if (goalDue) {
      pushIfInRange({
        event_id: `goal_${goalId}`,
        goal_id: goalId,
        title: goalTitle,
        description: goalDescription,
        start_at: `${goalDue}T09:00:00`,
        end_at: null,
        all_day: false,
        location: "",
        category: "goal",
        meta: { kind: "goal", read_only: true, source: "goal_due" },
        read_only: true,
      });
    }

    const milestoneLists = [
      goal?.milestones,
      goal?.goal_milestones,
      goal?.milestone_items,
      goal?.progress_milestones,
      goal?.sub_goals,
    ].filter(Array.isArray);

    for (const milestoneList of milestoneLists) {
      for (const milestone of milestoneList) {
        const milestoneId = milestone?.milestone_id ?? milestone?.id ?? milestone?._id;
        const milestoneTitle = milestone?.title ?? milestone?.name ?? milestone?.label ?? "Goal milestone";
        const milestoneDue =
          normalizeDateOnly(milestone?.due_date) ||
          normalizeDateOnly(milestone?.due_at) ||
          normalizeDateOnly(milestone?.target_date) ||
          normalizeDateOnly(milestone?.deadline) ||
          normalizeDateOnly(milestone?.end_date);
        if (!milestoneDue) continue;
        pushIfInRange({
          event_id: `goal_${goalId}_milestone_${milestoneId || milestoneTitle}`,
          goal_id: goalId,
          milestone_id: milestoneId || null,
          title: `${goalTitle}: ${milestoneTitle}`,
          description: milestone?.description || milestone?.notes || goalDescription || "",
          start_at: `${milestoneDue}T09:00:00`,
          end_at: null,
          all_day: false,
          location: "",
          category: "goal_milestone",
          meta: {
            kind: "goal_milestone",
            read_only: true,
            source: "goal_milestone_due",
            goal_title: goalTitle,
          },
          read_only: true,
        });
      }
    }
  }

  items.sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
  return items;
}



function getEventKind(ev) {
  const metaKind = String(ev?.meta?.kind || "").toLowerCase().trim();
  const category = String(ev?.category || "").toLowerCase().trim();
  const title = String(ev?.title || "").toLowerCase().trim();
  if (ev?.habit_id || metaKind === "habit" || title.startsWith("habit:")) return "habit";
  if (metaKind === "goal_milestone" || category === "goal_milestone") return "goal_milestone";
  if (metaKind === "goal" || category === "goal") return "goal";
  if (category === "workout" || metaKind === "workout" || metaKind === "workout_event" || metaKind === "workoutplan") return "workout";
  return category || metaKind || "event";
}

function cleanHabitTitle(title) {
  return String(title || "").replace(/^habit:\s*/i, "").trim() || "Habit";
}

function isHabitLikeEvent(ev) {
  return getEventKind(ev) === "habit";
}

function isCompletedEvent(ev) {
  const metricMet = ev?.metric_met ?? ev?.meta?.metric_met;
  const hasMetric =
    metricMet === true ||
    metricMet === false ||
    ev?.metric_rule != null ||
    ev?.meta?.metric_rule != null ||
    ev?.metric_actual != null ||
    ev?.meta?.metric_actual != null;

  if (hasMetric) return metricMet === true;

  return Boolean(ev?.completed || ev?.meta?.completed || ev?.is_completed || ev?.checked);
}

function normalizeHabitCalendarItem(ev) {
  return {
    ...ev,
    title: cleanHabitTitle(ev?.title),
    category: "habit",
    read_only: true,
    meta: {
      ...(ev?.meta || {}),
      kind: "habit",
      read_only: true,
      completed: isCompletedEvent(ev),
      metric_met: ev?.metric_met ?? ev?.meta?.metric_met,
      metric_rule: ev?.metric_rule ?? ev?.meta?.metric_rule ?? null,
      metric_actual: ev?.metric_actual ?? ev?.meta?.metric_actual ?? null,
    },
  };
}

function ProgressWheel({ pct = 0, label, sublabel, tone = "var(--accent)" }) {
  const safePct = Math.max(0, Math.min(100, Number(pct) || 0));
  const size = 110;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference * (safePct / 100);

  return (
    <div className="calendar-progress-wheel-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="calendar-progress-wheel">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={tone}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="calendar-progress-wheel-center">
        <div className="calendar-progress-wheel-value">{Math.round(safePct)}%</div>
        {label ? <div className="calendar-progress-wheel-label">{label}</div> : null}

      </div>
    </div>
  );
}

export default function CalendarView({ token, authUser, adminUsers, onStartWorkout, showToast }) {
  const isAdmin = !!authUser?.is_admin;
  const isTrainer = !!authUser?.is_trainer;

  const [viewUserId, setViewUserId] = useState(
    isAdmin ? authUser.user_id : isTrainer ? "" : authUser.user_id
  );
  const [createUserId, setCreateUserId] = useState(
    isAdmin ? authUser.user_id : isTrainer ? "" : authUser.user_id
  );

  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [googleSyncing, setGoogleSyncing] = useState(false);

  const [expandedDayKey, setExpandedDayKey] = useState(null);

  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);
  const [selectedEventDetail, setSelectedEventDetail] = useState(null);
  const [workoutLibrary, setWorkoutLibrary] = useState([]);
  const [workoutQuery, setWorkoutQuery] = useState("");
  const [subItemsDraft, setSubItemsDraft] = useState([]);
  const [createAllDay, setCreateAllDay] = useState(false);

  const [trainerClients, setTrainerClients] = useState([]);
  const [goalCalendarItems, setGoalCalendarItems] = useState([]);
  const [rawGoals, setRawGoals] = useState([]);
  const [detailProgress, setDetailProgress] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailActionBusy, setDetailActionBusy] = useState(false);

  const todayCellRef = useRef(null);
  const monthbarShellRef = useRef(null);
  const monthbarRef = useRef(null);
  const monthbarPinRafRef = useRef(0);
  const [isMonthbarPinned, setIsMonthbarPinned] = useState(false);
  const [monthbarPlaceholderHeight, setMonthbarPlaceholderHeight] = useState(0);

  const apiBase = useMemo(() => {
    if (isTrainer) return `${BACKEND_URL}/api/trainer/calendar`;
    return `${BACKEND_URL}/api/calendar`;
  }, [isTrainer]);

  const effectiveViewUserId = useMemo(() => {
    if (isTrainer) return viewUserId;
    if (!isAdmin) return authUser.user_id;
    return viewUserId;
  }, [isTrainer, isAdmin, authUser.user_id, viewUserId]);

  const gridStart = useMemo(() => startOfCalendarGrid(month), [month]);
  const gridEnd = useMemo(() => endOfCalendarGrid(month), [month]);

  const gridDays = useMemo(() => {
    const days = [];
    for (let d = new Date(gridStart); d <= gridEnd; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  }, [gridStart, gridEnd]);

  const selectedDayKey = toISODate(selectedDay);

  const loadWorkoutLibrary = async (q = "") => {
    if (!token) return;
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      const res = await fetch(`${BACKEND_URL}/api/workouts/library?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = await res.json();
      setWorkoutLibrary(data.items || []);
    } catch (e) {
      console.warn("Workout library load failed:", e?.message || e);
    }
  };

  const loadGoalCalendarItems = useCallback(async () => {
    if (!token) return;
    try {
      const opts = {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      };
      const candidates = [
        `${BACKEND_URL}/api/goals/list?user_id=me`,
        `${BACKEND_URL}/api/goals/list`,
        `${BACKEND_URL}/api/goals`,
      ];
      let payload = null;
      for (const url of candidates) {
        try {
          const res = await fetch(url, opts);
          if (!res.ok) continue;
          const raw = await res.text();
          payload = raw ? JSON.parse(raw) : null;
          if (payload) break;
        } catch (e) {
          console.warn("Goal calendar source failed:", e?.message || e);
        }
      }
      const rawGoals =
        (Array.isArray(payload?.goals) && payload.goals) ||
        (Array.isArray(payload?.items) && payload.items) ||
        (Array.isArray(payload) && payload) ||
        [];
      setRawGoals(rawGoals);
      setGoalCalendarItems(buildGoalCalendarItems(rawGoals, gridStart, gridEnd));
    } catch (e) {
      console.warn("Goal calendar load failed:", e?.message || e);
      setRawGoals([]);
      setGoalCalendarItems([]);
    }
  }, [token, gridStart, gridEnd]);

  const loadTrainerClients = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/trainer/clients`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = await res.json();
      setTrainerClients(data.clients || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (token) loadWorkoutLibrary();
    if (isTrainer && token) loadTrainerClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrainer, token]);

  useEffect(() => {
    const today = new Date();
    const viewingCurrentMonth =
      month.getFullYear() === today.getFullYear() &&
      month.getMonth() === today.getMonth();

    if (viewingCurrentMonth) {
      setSelectedDay(today);
    }
  }, [month]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_calendar_connected") === "1") {
      showToast?.("Google Calendar connected.", "success");
      params.delete("google_calendar_connected");
      const qs = params.toString();
      const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", nextUrl);
    }
  }, [showToast]);

  useEffect(() => {
    if (isTrainer && trainerClients.length > 0) {
      if (!viewUserId) setViewUserId(trainerClients[0].user_id);
      if (!createUserId) setCreateUserId(trainerClients[0].user_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainerClients]);

  useEffect(() => {
    if (loading) return;

    const today = new Date();
    const viewingCurrentMonth =
      month.getFullYear() === today.getFullYear() &&
      month.getMonth() === today.getMonth();

    if (!viewingCurrentMonth) return;
    if (!todayCellRef.current) return;

    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        todayCellRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [loading, events, month]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("start", toISODate(gridStart));
      params.set("end", toISODate(gridEnd));

      if (isTrainer) {
        if (!effectiveViewUserId) return;
        params.set("client_id", effectiveViewUserId);
      } else if (isAdmin) {
        if (effectiveViewUserId === "me") params.set("user_id", authUser.user_id);
        else if (effectiveViewUserId !== "all") params.set("user_id", effectiveViewUserId);
      }


      const res = await fetch(`${apiBase}/events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e) {
      showToast?.(`Calendar load failed: ${e.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, month, effectiveViewUserId]);

  useEffect(() => {
    if (token) loadGoalCalendarItems();
  }, [token, loadGoalCalendarItems]);

  const combinedEvents = useMemo(() => {
    const baseEvents = [];
    const habitItems = [];
    for (const ev of events) {
      if (isHabitLikeEvent(ev)) habitItems.push(normalizeHabitCalendarItem(ev));
      else baseEvents.push(ev);
    }

    const merged = [...baseEvents, ...habitItems, ...goalCalendarItems];
    merged.sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
    return merged;
  }, [events, goalCalendarItems]);

  const eventsByDate = useMemo(() => {
    const m = {};
    for (const ev of combinedEvents) {
      const k = isoDateFromStartAt(ev.start_at);
      if (!k) continue;
      if (!m[k]) m[k] = [];
      m[k].push(ev);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
    }
    return m;
  }, [combinedEvents]);

  const syncToGoogleCalendar = async () => {
    if (googleSyncing) return;

    setGoogleSyncing(true);
    try {
      const params = new URLSearchParams();

      if (isTrainer) {
        if (!effectiveViewUserId) {
          showToast?.("Select a client first.", "error");
          return;
        }
        params.set("user_id", effectiveViewUserId);
      } else if (isAdmin) {
        if (effectiveViewUserId === "me") params.set("user_id", authUser.user_id);
        else if (effectiveViewUserId !== "all") params.set("user_id", effectiveViewUserId);
      }

      const res = await fetch(`${BACKEND_URL}/api/calendar/google/sync?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 409) {
        const oauthRes = await fetch(`${BACKEND_URL}/api/calendar/google/auth-url`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!oauthRes.ok) throw new Error(await oauthRes.text());

        const oauthData = await oauthRes.json();
        if (oauthData?.auth_url) {
          window.location.href = oauthData.auth_url;
          return;
        }
        throw new Error("Could not start Google Calendar authorization.");
      }

      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      showToast?.(
        `Synced ${data.created_count || 0} events${data.updated_count ? `, updated ${data.updated_count}` : ""} to Google Calendar.`,
        "success"
      );
    } catch (e) {
      showToast?.(`Google Calendar sync failed: ${e.message}`, "error");
    } finally {
      setGoogleSyncing(false);
    }
  };

  const dayEvents = useMemo(() => {
    return eventsByDate[selectedDayKey] || [];
  }, [eventsByDate, selectedDayKey]);

  const selectedDayHeading = useMemo(() => formatDayHeading(selectedDay), [selectedDay]);

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateAllDay(false);
  };

  const openCreateForDate = (dateLike) => {
    const date = dateLike instanceof Date ? new Date(dateLike) : new Date(`${dateLike}T00:00:00`);
    setSelectedDay(date);
    setCreateAllDay(false);
    setSubItemsDraft([]);
    setWorkoutQuery("");
    setCreateOpen(true);
  };

  const openCreate = () => {
    openCreateForDate(selectedDay);
  };

  const openDayView = (d) => {
    setSelectedDay(d);
    setDayOpen(true);
  };
  const closeDayView = () => setDayOpen(false);

  const createEvent = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);

    const allDay = fd.get("all_day") === "on";
    let start_at = null;
    let end_at = null;

    if (allDay) {
      const startDate = fd.get("start_date");
      const endDate = fd.get("end_date");
      start_at = isoStartOfDay(startDate);
      end_at = endDate ? isoStartOfDay(endDate) : isoEndOfDayExclusive(startDate);
    } else {
      start_at = fd.get("start_at");
      end_at = fd.get("end_at") || null;
    }

    const payload = {
      user_id: isTrainer
        ? createUserId
        : isAdmin && createUserId === "me"
        ? authUser.user_id
        : isAdmin
        ? createUserId
        : undefined,
      title: fd.get("title"),
      description: fd.get("description") || "",
      start_at,
      end_at,
      all_day: allDay,
      timezone: "America/Los_Angeles",
      location: fd.get("location") || "",
      category: fd.get("category") || "",
    };

    if (isTrainer && !payload.user_id) return showToast?.("Select a client to create events for.", "error");
    if (!payload.start_at) return showToast?.("Please select a start date/time.", "error");

    const res = await fetch(`${apiBase}/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return showToast?.(`Create failed: ${await res.text()}`, "error");

    closeCreate();
    await loadEvents();
  };

  const updateEvent = async (event_id, updates) => {
    const res = await fetch(`${apiBase}/events/${event_id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(updates),
    });
    if (!res.ok) return showToast?.(`Update failed: ${await res.text()}`);
    setEditing(null);
    await loadEvents();
  };

  const deleteEvent = async (event_id) => {
    const res = await fetch(`${apiBase}/events/${event_id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return showToast?.(`Delete failed: ${await res.text()}`, "error");
    await loadEvents();
  };

  const deleteCurrentMonthEvents = async () => {
    const monthStart = toISODate(startOfMonth(month));
    const monthEnd = toISODate(endOfMonth(month));
    const label = month.toLocaleString(undefined, { month: "long", year: "numeric" });
    const ok = window.confirm(`Delete all calendar events in ${label}? This cannot be undone.`);
    if (!ok) return;

    try {
      const params = new URLSearchParams();
      params.set("start", monthStart);
      params.set("end", monthEnd);

      if (isTrainer) {
        if (!createUserId) return showToast?.("Select a client first.", "error");
        params.set("user_id", createUserId);
      } else if (isAdmin) {
        if (createUserId === "me") params.set("user_id", authUser.user_id);
        else if (createUserId && createUserId !== "all") params.set("user_id", createUserId);
      }

      const res = await fetch(`${apiBase}/events?${params.toString()}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      await loadEvents();
    } catch (e) {
      showToast?.(`Delete month failed: ${e.message}`, "error");
    }
  };

  const dayAgenda = useMemo(() => {
    const allDay = [];
    const timed = [];
    for (const ev of dayEvents) {
      if (ev.all_day) allDay.push(ev);
      else timed.push(ev);
    }
    timed.sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());

    const buckets = Array.from({ length: 24 }, (_, hour) => ({ hour, items: [] }));
    for (const ev of timed) {
      const dt = new Date(ev.start_at);
      const h = Number.isNaN(dt.getTime()) ? 0 : dt.getHours();
      buckets[Math.max(0, Math.min(23, h))].items.push(ev);
    }
    return { allDay, buckets };
  }, [dayEvents]);


  useEffect(() => {
    let cancelled = false;

    const loadDetailProgress = async () => {
      if (!selectedEventDetail || !token) {
        setDetailProgress(null);
        return;
      }

      const kind = getEventKind(selectedEventDetail);
      setDetailLoading(true);

      try {
        if (kind === "habit" && selectedEventDetail.habit_id) {
          const qs = new URLSearchParams();
          qs.set("user_id", "me");
          qs.set("days", "1");
          const res = await fetch(`${BACKEND_URL}/api/habits/${encodeURIComponent(selectedEventDetail.habit_id)}/progress?${qs.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store",
          });
          if (!res.ok) throw new Error(await res.text());
          const out = await res.json();

          const hasMetric = out?.metric_rule || out?.metric_actual != null || out?.metric_met != null;
          const completed = Boolean(out?.completed || out?.completed_count || selectedEventDetail?.meta?.completed || selectedEventDetail?.completed);
          const pct = hasMetric
            ? Math.max(0, Math.min(100, Number(out?.metric_met ? 100 : 0) || 0 || 0))
            : completed
            ? 100
            : 0;

          const helper = hasMetric
            ? `${out?.metric_rule?.domain || ""}.${out?.metric_rule?.field || ""} ${out?.metric_rule?.operator || ""} ${out?.metric_rule?.value ?? ""}`.trim()
            : completed
            ? "Completed"
            : "Not completed";

          if (!cancelled) {
            setDetailProgress({
              kind,
              mode: hasMetric ? "metric" : "checkbox",
              pct,
              completed,
              helper,
              actual: out?.metric_actual ?? null,
              metric_rule: out?.metric_rule || null,
            });
          }
          return;
        }

        if ((kind === "goal" || kind === "goal_milestone") && selectedEventDetail.goal_id) {
          const goal = (rawGoals || []).find((g) => String(g?.goal_id ?? g?.id ?? g?._id) === String(selectedEventDetail.goal_id));
          if (goal) {
            const metricMet = goal?.metric_met;
            const metricActual = goal?.metric_actual;
            const hasMetric = metricMet === true || metricMet === false || metricActual != null;
            const pct = hasMetric
              ? metricMet === true
                ? 100
                : 0
              : Number(goal?.pct_complete || 0);
            const helper = hasMetric
              ? `${goal?.metric_rule?.domain || ""}.${goal?.metric_rule?.field || ""} ${goal?.metric_rule?.operator || ""} ${goal?.metric_rule?.value ?? ""}`.trim()
              : `${Number(goal?.completed_milestones || 0)} of ${Number(goal?.total_milestones || 0)} milestones`;

            if (!cancelled) {
              setDetailProgress({
                kind,
                mode: hasMetric ? "metric" : "milestones",
                pct,
                completed: hasMetric ? metricMet === true : Number(goal?.pct_complete || 0) >= 100,
                helper,
                actual: metricActual ?? null,
                metric_rule: goal?.metric_rule || null,
              });
            }
            return;
          }
        }

        if (!cancelled) setDetailProgress(null);
      } catch (e) {
        if (!cancelled) setDetailProgress(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    loadDetailProgress();
    return () => {
      cancelled = true;
    };
  }, [selectedEventDetail, token, rawGoals]);

  const toggleSelectedHabitComplete = async (checked) => {
    if (!selectedEventDetail?.habit_id || !token) return;
    setDetailActionBusy(true);
    try {
      const date = isoDateFromStartAt(selectedEventDetail.start_at) || toISODate(selectedDay);
      const res = await fetch(`${BACKEND_URL}/api/habits/${encodeURIComponent(selectedEventDetail.habit_id)}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ habit_id: selectedEventDetail.habit_id, date, checked: !!checked }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSelectedEventDetail((prev) => prev ? ({ ...prev, completed: !!checked, meta: { ...(prev.meta || {}), completed: !!checked } }) : prev);
      await loadEvents();
      showToast?.("Habit updated.", "success");
    } catch (e) {
      showToast?.(`Habit update failed: ${e?.message || e}`, "error");
    } finally {
      setDetailActionBusy(false);
    }
  };


  const updateMonthbarPinned = useCallback(() => {
    const shellEl = monthbarShellRef.current;
    const barEl = monthbarRef.current;
    if (!shellEl || !barEl || typeof window === "undefined") return;

    const isMobile = window.matchMedia("(max-width: 809px)").matches;
    if (!isMobile) {
      setIsMonthbarPinned(false);
      setMonthbarPlaceholderHeight(0);
      return;
    }

    const shellTop = shellEl.getBoundingClientRect().top;
    const barHeight = Math.ceil(barEl.getBoundingClientRect().height || barEl.offsetHeight || 0);
    const navEl = document.querySelector(".nav-header");
    const navHeight = Math.ceil(navEl?.getBoundingClientRect?.().height || navEl?.offsetHeight || 0);
    const pinTop = Math.max(0, navHeight);

    if (shellTop <= pinTop) {
      setIsMonthbarPinned(true);
      setMonthbarPlaceholderHeight(barHeight);
      return;
    }

    setIsMonthbarPinned(false);
    setMonthbarPlaceholderHeight(0);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const queuePinnedUpdate = () => {
      if (monthbarPinRafRef.current) return;
      monthbarPinRafRef.current = window.requestAnimationFrame(() => {
        monthbarPinRafRef.current = 0;
        updateMonthbarPinned();
      });
    };

    queuePinnedUpdate();
    window.addEventListener("scroll", queuePinnedUpdate, { passive: true });
    window.addEventListener("resize", queuePinnedUpdate);

    return () => {
      window.removeEventListener("scroll", queuePinnedUpdate);
      window.removeEventListener("resize", queuePinnedUpdate);
      if (monthbarPinRafRef.current) {
        window.cancelAnimationFrame(monthbarPinRafRef.current);
        monthbarPinRafRef.current = 0;
      }
    };
  }, [updateMonthbarPinned, month]);

  const monthLabel = month.toLocaleString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="form-section calendar-page">
      <div className="card calendar-header-card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <h3 className="heading-3" style={{ marginBottom: 0 }}>Calendar</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn-secondary" type="button" onClick={deleteCurrentMonthEvents}>Delete current month events</button>
            <button className="btn-primary" type="button" onClick={openCreate}>Add Event</button>
            <button className="btn-secondary" type="button" onClick={syncToGoogleCalendar} disabled={googleSyncing}>
              {googleSyncing ? "Syncing..." : "Sync to Google Calendar"}
            </button>
          </div>
        </div>
      </div>

      {(isAdmin || isTrainer) && (
        <div className="calendar-controls" style={{ marginTop: 12 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="body-medium">View</label>
            {isAdmin ? (
              <select className="form-input" value={viewUserId} onChange={(e) => setViewUserId(e.target.value)}>
                <option value="all">All users</option>
                <option value="me">Me</option>
                {(adminUsers || []).map((u) => (
                  <option key={u.auth_info.user_id} value={u.auth_info.user_id}>
                    {u.auth_info.name} ({u.auth_info.email})
                  </option>
                ))}
              </select>
            ) : (
              <select className="form-input" value={viewUserId} onChange={(e) => setViewUserId(e.target.value)}>
                <option value="">Select client</option>
                {trainerClients.map((c) => (
                  <option key={c.user_id} value={c.user_id}>
                    {c.name} ({c.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="body-medium">Create events for</label>
            {isAdmin ? (
              <select className="form-input" value={createUserId} onChange={(e) => setCreateUserId(e.target.value)}>
                <option value="me">Me</option>
                {(adminUsers || []).map((u) => (
                  <option key={u.auth_info.user_id} value={u.auth_info.user_id}>
                    {u.auth_info.name} ({u.auth_info.email})
                  </option>
                ))}
              </select>
            ) : (
              <select className="form-input" value={createUserId} onChange={(e) => setCreateUserId(e.target.value)}>
                <option value="">Select client</option>
                {trainerClients.map((c) => (
                  <option key={c.user_id} value={c.user_id}>
                    {c.name} ({c.email})
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      <div className="card calendar-month-card" style={{ padding: 16, marginTop: 12 }}>
        <div ref={monthbarShellRef} className={`calendar-monthbar-shell ${isMonthbarPinned ? "is-pinned" : ""}`}>
          <div
            className="calendar-monthbar-spacer"
            aria-hidden="true"
            style={{ height: isMonthbarPinned ? `${monthbarPlaceholderHeight}px` : 0 }}
          />
          <div
            ref={monthbarRef}
            className={`calendar-monthbar calendar-monthbar-sticky ${isMonthbarPinned ? "is-pinned" : ""}`}
          >
            <button className="btn-secondary" type="button" onClick={() => setMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}>◀</button>
            <div className="body-medium calendar-monthlabel">{monthLabel}</div>
            <button className="btn-secondary" type="button" onClick={() => setMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}>▶</button>
          </div>
        </div>

        <div
          className="calendar-grid"
          style={{
            marginTop: 12,
            alignItems: "stretch",
            gridAutoRows: "minmax(110px, auto)",
          }}
        >
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="calendar-dow">{d}</div>
          ))}

          {gridDays.map((d) => {
            const key = toISODate(d);
            const evs = eventsByDate[key] || [];
            const isThisMonth = inSameMonth(d, month);
            const isSelected = sameDay(d, selectedDay);
            const isExpanded = expandedDayKey === key;
            const isToday = sameDay(d, new Date());

            const maxCollapsed = 3;
            const visible = isExpanded ? evs : evs.slice(0, maxCollapsed);
            const hiddenCount = Math.max(0, evs.length - maxCollapsed);

            return (
              <div
                key={key}
                ref={isToday ? todayCellRef : null}
                role="button"
                tabIndex={0}
                onClick={() => openDayView(d)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openDayView(d);
                  }
                }}
                className={`calendar-day ${isThisMonth ? "in-month" : "out-month"} ${isSelected ? "selected" : ""}`}
                style={{
                  minWidth: 0,
                  height: isExpanded ? "auto" : undefined,
                  alignSelf: "stretch",
                  position: "relative",
                  cursor: "pointer",
                }}
              >

                <div className="calendar-dayhead" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
  <div>
    <div className="calendar-dayweekday">{d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase()}</div>
    <div className="calendar-daynum">{d.getDate()}</div>
  </div>

  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      setSelectedDay(d);
      openCreate();
    }}
    style={{
      width: 32,
      height: 32,
      borderRadius: "50%",
      border: "1px solid var(--border-light)",
      background: "var(--bg-card)",
      fontSize: 18,
      fontWeight: 700,
      lineHeight: "1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      padding: 0,
    }}
  >
    +
  </button>
</div>

                <div className="calendar-evlist" style={{ overflow: "visible" }}>
                  {visible.map((ev) => (
                    <div
                      key={ev.event_id}
                      className="calendar-evchip"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEventDetail(ev);
                      }}
                      title={`${ev.all_day ? "All day" : fmtTime(ev.start_at)}${ev.end_at ? `–${fmtTime(ev.end_at)}` : ""} • ${ev.title}${ev.description ? `\n\n${ev.description}` : ""}`}
                      style={{
                        whiteSpace: isExpanded ? "normal" : "nowrap",
                        overflow: "visible",
                        textOverflow: "clip",
                        lineHeight: 1.2,
                        paddingTop: 6,
                        paddingBottom: 6,
                      }}
                    >
                      <span
                        className="calendar-evtitle"
                        style={{
                          whiteSpace: isExpanded ? "normal" : "nowrap",
                          overflow: "hidden",
                          textOverflow: isExpanded ? "clip" : "ellipsis",
                          display: "block",
                        }}
                      >
                        {getEventKind(ev) === "habit" ? (
                          <span className="calendar-evtitle-inner">
                            {isCompletedEvent(ev) ? <span className="calendar-habit-check">✓</span> : null}
                            <span>{cleanHabitTitle(ev.title)}</span>
                          </span>
                        ) : (
                          ev.title
                        )}
                      </span>
                    </div>
                  ))}

                  {!isExpanded && hiddenCount > 0 && (
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{
                        marginTop: 6,
                        width: "100%",
                        padding: "6px 10px",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDayKey(key);
                      }}
                      title="Show all events for this day"
                    >
                      +{hiddenCount} more
                    </button>
                  )}

                  {isExpanded && evs.length > 0 && (
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{
                        marginTop: 6,
                        width: "100%",
                        padding: "6px 10px",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedDayKey(null);
                      }}
                      title="Collapse"
                    >
                      Collapse
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {dayOpen && (
        <div className="modal-overlay" onMouseDown={closeDayView}>
          <div className="modal-card calendar-modal-card" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0 }}>{selectedDayHeading}</h2>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  className="modal-close"
                  onClick={closeDayView}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
                <div className="body-medium" style={{ fontWeight: 700 }}>
                  {dayEvents.length} event{dayEvents.length === 1 ? "" : "s"}
                </div>
                <button className="btn-primary" type="button" onClick={() => { closeDayView(); openCreateForDate(selectedDay); }}>
                  Add Event
                </button>
              </div>

              {loading ? (
                <div className="body-medium">Loading…</div>
              ) : (
                <>
                  {dayAgenda.allDay.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div className="body-medium" style={{ fontWeight: 700, marginBottom: 8 }}>All day</div>
                      <div style={{ display: "grid", gap: 10 }}>
                        {dayAgenda.allDay.map((ev) => {
                          const readOnly = !!(ev.read_only || ev?.meta?.read_only);
                          return (
                          <div key={ev.event_id} className="calendar-agenda-card">
                            <div className="calendar-event-row">
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 700, whiteSpace: "normal" }}>
                                  {ev.title}
                                </div>
                                <div className="body-small" style={{ color: "var(--text-muted)" }}>
                                  All day
                                </div>
                                {ev.description ? (
                                  <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 6, whiteSpace: "pre-wrap" }}>
                                    {ev.description}
                                  </div>
                                ) : null}
                              </div>

                              <div className="calendar-event-actions">
                                <button className="btn-secondary" type="button" onClick={() => setSelectedEventDetail(ev)}>View</button>
                                {!readOnly ? (
                                  <>
                                    <button className="btn-secondary" type="button" onClick={() => setEditing(ev)}>Edit</button>
                                    <button className="btn-secondary" type="button" onClick={() => deleteEvent(ev.event_id)}>Delete</button>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );})}
                      </div>
                    </div>
                  )}

                  <div className="body-medium" style={{ fontWeight: 700, marginBottom: 8 }}>Timeline</div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {dayAgenda.buckets.map((b) => (
                      <div key={b.hour} style={{ border: "1px solid var(--border-light)", borderRadius: 12, padding: 12 }}>
                        <div className="body-small" style={{ color: "var(--text-muted)", fontWeight: 700 }}>
                          {pad2(b.hour)}:00
                        </div>

                        {b.items.length === 0 ? (
                          <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>—</div>
                        ) : (
                          <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                            {b.items.map((ev) => {
                              const readOnly = !!(ev.read_only || ev?.meta?.read_only);
                              return (
                              <div key={ev.event_id} className="calendar-agenda-card">
                                <div className="calendar-event-row">
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, whiteSpace: "normal" }}>
                                      {ev.title}
                                    </div>
                                    <div className="body-small" style={{ color: "var(--text-muted)" }}>
                                      {fmtTime(ev.start_at)}{ev.end_at ? ` → ${fmtTime(ev.end_at)}` : ""}
                                    </div>
                                    {ev.description ? (
                                      <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 6, whiteSpace: "pre-wrap" }}>
                                        {ev.description}
                                      </div>
                                    ) : null}
                                  </div>

                                  <div className="calendar-event-actions">
                                    <button className="btn-secondary" type="button" onClick={() => setSelectedEventDetail(ev)}>View</button>
                                    {!readOnly ? (
                                      <>
                                        <button className="btn-secondary" type="button" onClick={() => setEditing(ev)}>Edit</button>
                                        <button className="btn-secondary" type="button" onClick={() => deleteEvent(ev.event_id)}>Delete</button>
                                      </>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            );})}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedEventDetail && (
        <div className="modal-overlay" onMouseDown={() => setSelectedEventDetail(null)}>
          <div className="modal-card calendar-modal-card" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0 }}>{selectedEventDetail?.title || "Event"}</h2>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {!selectedEventDetail?.read_only && !selectedEventDetail?.meta?.read_only ? (
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = window.confirm("Delete this event?");
                      if (!ok) return;
                      await deleteEvent(selectedEventDetail.event_id);
                      setSelectedEventDetail(null);
                    }}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      border: "1px solid var(--border-light)",
                      background: "#ffefef",
                      color: "#d00",
                      fontSize: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                    title="Delete event"
                  >
                    🗑
                  </button>
                ) : null}

                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setSelectedEventDetail(null)}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="modal-body">
              <div className="body-small" style={{ color: "var(--text-muted)", marginBottom: 12 }}>
                {selectedEventDetail.all_day ? "All day" : `${fmtTime(selectedEventDetail.start_at)}${selectedEventDetail.end_at ? ` → ${fmtTime(selectedEventDetail.end_at)}` : ""}`}
                {selectedEventDetail.location ? ` • ${selectedEventDetail.location}` : ""}
              </div>

              {(() => {
                const kind = getEventKind(selectedEventDetail);
                const isWorkout = kind === "workout";
                const isGoalLike = kind === "goal" || kind === "goal_milestone";
                const isHabit = kind === "habit";
                const isCheckboxHabit = isHabit && detailProgress?.mode === "checkbox";
                const completed = Boolean(
                  detailProgress?.completed ??
                  selectedEventDetail?.meta?.completed ??
                  selectedEventDetail?.completed ??
                  false
                );

                return (
                  <>
                    {isWorkout ? (
                      <button className="btn-primary" type="button" onClick={() => onStartWorkout?.(selectedEventDetail.event_id)}>
                        Start Workout
                      </button>
                    ) : null}

                    {selectedEventDetail.description ? (
                      <div className="body-medium" style={{ whiteSpace: "pre-wrap", marginBottom: 14 }}>
                        {selectedEventDetail.description}
                      </div>
                    ) : null}

                    {(isGoalLike || isHabit) ? (
                      <div className="calendar-detail-progress-card">
                        {detailLoading ? (
                          <div className="body-small" style={{ color: "var(--text-muted)" }}>Loading progress…</div>
                        ) : (
                          <>
                            <ProgressWheel
                              pct={Number(detailProgress?.pct ?? (completed ? 100 : 0))}
                              label={isHabit ? "Today's progress" : "Goal progress"}
                              sublabel={detailProgress?.helper || (completed ? "Completed" : "In progress")}
                              tone={completed ? "#22c55e" : "var(--accent)"}
                            />

                            {detailProgress?.actual != null ? (
                              <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 10 }}>
                                Actual: {detailProgress.actual}
                              </div>
                            ) : null}

                            {isCheckboxHabit ? (
                              <button
                                type="button"
                                className={completed ? "btn-secondary" : "btn-primary"}
                                disabled={detailActionBusy}
                                onClick={() => toggleSelectedHabitComplete(!completed)}
                                style={{ marginTop: 14 }}
                              >
                                {detailActionBusy ? "Saving…" : completed ? "Undo check off" : "Check off"}
                              </button>
                            ) : null}
                          </>
                        )}
                      </div>
                    ) : Array.isArray(selectedEventDetail.sub_items) && selectedEventDetail.sub_items.length > 0 ? (
                      <div>
                        <div className="heading-4" style={{ marginBottom: 10 }}>Workout items</div>
                        <div style={{ display: "grid", gap: 10 }}>
                          {selectedEventDetail.sub_items.map((si, idx) => (
                            <div key={si.item_id || idx} style={{ border: "1px solid var(--border-light)", borderRadius: 12, padding: 12, background: "var(--bg-section)" }}>
                              <div style={{ fontWeight: 800 }}>{si.name}</div>
                              <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 6 }}>
                                {si.sets != null ? `${si.sets} sets` : ""}{si.reps != null ? ` • ${si.reps} reps` : ""}{si.duration_minutes != null ? ` • ${si.duration_minutes} min` : ""}
                                {si.weight != null ? ` • ${si.weight}` : ""}
                              </div>
                              {si.notes ? (
                                <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6, whiteSpace: "pre-wrap" }}>
                                  {si.notes}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="body-small" style={{ color: "var(--text-muted)" }}>
                        No sub-items for this event.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-overlay" onMouseDown={() => setEditing(null)}>
          <div className="modal-card calendar-modal-card" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0 }}>{editing?.title || "Event"}</h2>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = window.confirm("Delete this event?");
                    if (!ok) return;
                    await deleteEvent(editing.event_id);
                    setEditing(null);
                  }}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: "1px solid var(--border-light)",
                    background: "#ffefef",
                    color: "#d00",
                    fontSize: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  title="Delete event"
                >
                  🗑
                </button>

                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setEditing(null)}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="modal-body">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const fd = new FormData(e.target);

                  const allDay = fd.get("all_day") === "on";
                  let start_at = null;
                  let end_at = null;

                  if (allDay) {
                    const sd = fd.get("start_date");
                    const ed = fd.get("end_date");
                    start_at = isoStartOfDay(sd);
                    end_at = ed ? isoStartOfDay(ed) : isoEndOfDayExclusive(sd);
                  } else {
                    start_at = fd.get("start_at");
                    end_at = fd.get("end_at") || null;
                  }

                  updateEvent(editing.event_id, {
                    title: fd.get("title"),
                    description: fd.get("description") || "",
                    all_day: allDay,
                    start_at,
                    end_at,
                  });
                }}
              >
                <div className="form-group">
                  <label className="body-medium">Title</label>
                  <input name="title" className="form-input" defaultValue={editing.title} required />
                </div>

                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="body-small" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" name="all_day" defaultChecked={!!editing.all_day} />
                    All day
                  </label>
                </div>

                {editing.all_day ? (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="body-medium">Start date</label>
                      <input name="start_date" type="date" className="form-input" defaultValue={toLocalDateInputValue(editing.start_at)} required />
                    </div>
                    <div className="form-group">
                      <label className="body-medium">End date (optional)</label>
                      <input name="end_date" type="date" className="form-input" defaultValue={editing.end_at ? toLocalDateInputValue(editing.end_at) : ""} />
                    </div>
                  </div>
                ) : (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="body-medium">Start</label>
                      <input name="start_at" type="datetime-local" className="form-input" defaultValue={toLocalDatetimeInputValue(editing.start_at)} required />
                    </div>
                    <div className="form-group">
                      <label className="body-medium">End</label>
                      <input name="end_at" type="datetime-local" className="form-input" defaultValue={editing.end_at ? toLocalDatetimeInputValue(editing.end_at) : ""} />
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="body-medium">Description</label>
                  <textarea name="description" className="form-input" rows={3} defaultValue={editing.description || ""} />
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                  <button type="submit" className="btn-primary">Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="modal-overlay" onMouseDown={closeCreate}>
          <div className="modal-card calendar-modal-card" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Event</h2>
              <button type="button" className="modal-close" onClick={closeCreate}>✕</button>
            </div>

            <div className="modal-body">
              <form onSubmit={createEvent}>
                <div className="form-group">
                  <label className="body-medium">Title</label>
                  <input name="title" className="form-input" required />
                </div>

                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="body-small" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      name="all_day"
                      checked={createAllDay}
                      onChange={(e) => setCreateAllDay(e.target.checked)}
                    />
                    All day
                  </label>
                </div>

                {createAllDay ? (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="body-medium">Start date</label>
                      <input name="start_date" type="date" className="form-input" required defaultValue={selectedDayKey} />
                    </div>
                    <div className="form-group">
                      <label className="body-medium">End date (optional)</label>
                      <input name="end_date" type="date" className="form-input" />
                      <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
                        Leave blank for 1-day all-day event.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="body-medium">Start</label>
                      <input name="start_at" type="datetime-local" className="form-input" required defaultValue={`${selectedDayKey}T09:00`} />
                    </div>
                    <div className="form-group">
                      <label className="body-medium">End (optional)</label>
                      <input name="end_at" type="datetime-local" className="form-input" />
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="body-medium">Description</label>
                  <textarea name="description" className="form-input" rows={3} />
                </div>

                <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-light)" }}>
                  <div className="heading-4" style={{ marginBottom: 8 }}>Workout items (optional)</div>
                  <div className="body-small" style={{ color: "var(--text-muted)", marginBottom: 10 }}>
                    Add sub-items to this event (exercises, sets/reps, notes). You can browse the workout library.
                  </div>

                  <div className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "end" }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="body-medium">Search library</label>
                      <input
                        className="form-input"
                        value={workoutQuery}
                        onChange={(e) => setWorkoutQuery(e.target.value)}
                        placeholder="Search exercises…"
                      />
                    </div>
                    <button type="button" className="btn-secondary" onClick={() => loadWorkoutLibrary(workoutQuery)} style={{ height: 48 }}>
                      Search
                    </button>
                  </div>

                  <div className="form-group" style={{ marginTop: 10 }}>
                    <label className="body-medium">Add from library</label>
                    <select
                      className="form-input"
                      onChange={(e) => {
                        const id = e.target.value;
                        if (!id) return;
                        const it = (workoutLibrary || []).find((x) => x.exercise_id === id);
                        if (!it) return;
                        setSubItemsDraft((prev) => [
                          ...prev,
                          { item_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`, exercise_id: it.exercise_id, name: it.name, sets: 3, reps: 10, notes: "" },
                        ]);
                        e.target.value = "";
                      }}
                      defaultValue=""
                    >
                      <option value="">Select an exercise…</option>
                      {(workoutLibrary || []).slice(0, 200).map((it) => (
                        <option key={it.exercise_id} value={it.exercise_id}>
                          {it.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {subItemsDraft.length > 0 && (
                    <div style={{ display: "grid", gap: 10 }}>
                      {subItemsDraft.map((si, idx) => (
                        <div key={si.item_id || idx} style={{ border: "1px solid var(--border-light)", borderRadius: 12, padding: 12, background: "var(--bg-section)" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                            <div style={{ fontWeight: 800 }}>{si.name}</div>
                            <button type="button" className="btn-secondary" onClick={() => setSubItemsDraft((p) => p.filter((_, i) => i !== idx))}>
                              Remove
                            </button>
                          </div>
                          <div className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label className="body-small">Sets</label>
                              <input className="form-input" type="number" value={si.sets ?? ""} onChange={(e) => {
                                const v = e.target.value;
                                setSubItemsDraft((p) => p.map((x, i) => i === idx ? { ...x, sets: v === "" ? null : Number(v) } : x));
                              }} />
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label className="body-small">Reps</label>
                              <input className="form-input" type="number" value={si.reps ?? ""} onChange={(e) => {
                                const v = e.target.value;
                                setSubItemsDraft((p) => p.map((x, i) => i === idx ? { ...x, reps: v === "" ? null : Number(v) } : x));
                              }} />
                            </div>
                          </div>
                          <div className="form-group" style={{ marginTop: 10, marginBottom: 0 }}>
                            <label className="body-small">Notes (optional)</label>
                            <input className="form-input" value={si.notes || ""} onChange={(e) => {
                              const v = e.target.value;
                              setSubItemsDraft((p) => p.map((x, i) => i === idx ? { ...x, notes: v } : x));
                            }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={closeCreate}>Cancel</button>
                  <button type="submit" className="btn-primary">Add</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
